/**
 * Batch screen: paste a bank's identifiers, run POST /mulehunter/screen, show
 * each hit with its payment-rail attribution and the by-PSP-bank breakdown.
 * Pure read; every hit is a scored lead, never proof.
 */
import { useState } from 'react';
import { Loader2, ScanSearch, X } from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import { SeverityBadge } from '../intel';
import {
    mulehunterService,
    type MulehunterScreenResponse,
    type MulehunterScreenResult,
} from '../../services/api';
import { PspBankSummary, RailChip } from './RailAttribution';

const MAX_ITEMS = 5000;
const KNOWN_TYPES = new Set([
    'upi', 'phone', 'bank', 'ifsc', 'pan', 'wallet', 'tx_hash',
    'handle', 'url', 'domain', 'onion', 'email', 'utr',
]);

/** One identifier per line. An optional `type,` or `type<TAB>` prefix pins the
 *  type; otherwise the backend infers it. */
export function parseScreenInput(text: string): { value: string; type?: string }[] {
    const out: { value: string; type?: string }[] = [];
    for (const line of text.split(/\r?\n/)) {
        const s = line.trim();
        if (!s) continue;
        const m = s.match(/^([a-z_]+)\s*[,\t]\s*(.+)$/i);
        if (m && KNOWN_TYPES.has(m[1].toLowerCase())) {
            out.push({ type: m[1].toLowerCase(), value: m[2].trim() });
        } else {
            out.push({ value: s });
        }
    }
    return out;
}

const FRESH_STYLE: Record<string, string> = {
    active: 'text-emerald-700 dark:text-emerald-400',
    recent: 'text-amber-700 dark:text-amber-400',
    stale: 'text-muted-foreground',
    undated: 'text-muted-foreground',
};

export function ScreenResultsTable({ results }: { results: MulehunterScreenResult[] }) {
    return (
        <div className="border border-border rounded-md overflow-x-auto bg-card">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium">Input</th>
                        <th className="px-3 py-2 font-medium">Result</th>
                        <th className="px-3 py-2 font-medium">Rail</th>
                        <th className="px-3 py-2 font-medium">Risk</th>
                        <th className="px-3 py-2 font-medium">Last seen</th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((r, i) => (
                        <tr key={`${r.type}:${r.canonical}:${i}`} className="border-b border-border last:border-0">
                            <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.type}</td>
                            <td className="px-3 py-1.5 font-mono text-xs text-foreground max-w-[260px] truncate" title={r.canonical}>{r.input}</td>
                            <td className="px-3 py-1.5 text-xs">
                                {r.matched ? (
                                    <span
                                        title={`${r.origin_count} independent origin(s) · ${r.source_count} source type(s): ${r.sources.join(', ')}`}
                                        className="text-foreground"
                                    >
                                        Hit · {r.occurrences.toLocaleString('en-IN')} mention{r.occurrences === 1 ? '' : 's'}
                                        {r.corroborated && <span className="ml-1 text-primary font-semibold">· corroborated</span>}
                                    </span>
                                ) : (
                                    <span className="text-muted-foreground">No match</span>
                                )}
                            </td>
                            <td className="px-3 py-1.5">
                                {r.matched ? <RailChip idType={r.type} rail={r.rail_attribution} /> : <span className="text-muted-foreground text-xs">—</span>}
                            </td>
                            <td className="px-3 py-1.5">
                                {r.matched && r.risk_hint
                                    ? <SeverityBadge level={r.risk_hint === 'phishing' ? 'critical' : r.risk_hint} label={r.risk_hint} />
                                    : <span className="text-muted-foreground text-xs">—</span>}
                            </td>
                            <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                                {r.matched ? (
                                    <span className={FRESH_STYLE[r.freshness] || 'text-muted-foreground'}>
                                        {r.freshness}{r.days_since_last_seen != null ? ` · ${r.days_since_last_seen}d` : ''}
                                    </span>
                                ) : <span className="text-muted-foreground">—</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function BatchScreenPanel({ onClose }: { onClose: () => void }) {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState<MulehunterScreenResponse | null>(null);

    const items = parseScreenInput(text);
    const tooMany = items.length > MAX_ITEMS;

    const run = async () => {
        if (!items.length || tooMany) return;
        setBusy(true);
        try {
            setRes(await mulehunterService.screen(items));
        } catch {
            toast.error('Screen failed');
        } finally {
            setBusy(false);
        }
    };

    const s = res?.summary;
    // Hits first, then misses, keeping input order within each group.
    const ordered = res ? [...res.results.filter(r => r.matched), ...res.results.filter(r => !r.matched)] : [];

    return (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <ScanSearch className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground">Screen a batch against the harvest index</h2>
                <button onClick={onClose} className="ml-auto text-muted-foreground hover:text-foreground" title="Close">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <p className="text-xs text-muted-foreground max-w-3xl">
                One identifier per line (VPA, phone, account, IFSC, UTR, wallet, handle…). Prefix a line with
                <span className="font-mono"> type,</span> to pin the type, e.g. <span className="font-mono">utr,612345678901</span>.
                Hits are scored leads from harvested OSINT, not proof. Nothing is written.
            </p>
            <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={5}
                aria-label="Identifiers to screen"
                placeholder={'9876543210@okicici\n+44 7700 900123\nHDFC0001234'}
                className="w-full px-2.5 py-1.5 text-xs font-mono rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="flex items-center gap-2">
                <button
                    onClick={run}
                    disabled={busy || items.length === 0 || tooMany}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm hover:opacity-90 disabled:opacity-60"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />} Screen
                </button>
                <span className={clsx('text-xs', tooMany ? 'text-red-500' : 'text-muted-foreground')}>
                    {items.length.toLocaleString('en-IN')} identifier{items.length === 1 ? '' : 's'}
                    {tooMany && ` — max ${MAX_ITEMS.toLocaleString('en-IN')} per batch`}
                </span>
            </div>

            {s && (
                <div className="text-xs text-muted-foreground">
                    <b className="text-foreground">{s.matched}</b> of {s.resolved} resolved matched
                    ({(s.match_rate * 100).toFixed(1)}%) · <b className="text-foreground">{s.corroborated_hits}</b> corroborated
                    (2+ independent origins) · {s.actor_associated_hits} posted by classified actors
                    {s.unresolved > 0 && <> · {s.unresolved} unrecognised</>}
                </div>
            )}
            {s && <PspBankSummary byPspBank={s.by_psp_bank} />}
            {res && ordered.length > 0 && <ScreenResultsTable results={ordered} />}
        </div>
    );
}
